import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, CreditCard, Sparkles, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth-context";
import { billing } from "@/lib/api";

const planFeatures = {
  starter: ["1.000 customer database", "3 QR campaign aktif", "3 WhatsApp campaign/bulan", "Basic dashboard"],
  growth: ["10.000 customer database", "Unlimited QR campaign", "WhatsApp automation", "Repeat order reminder", "Campaign template library"],
  pro: ["50.000 customer database", "AI Marketing Assistant", "AI Segmentation", "Referral & Membership", "SKU analytics", "Benchmark kategori"],
};

const STATUS_STYLE = {
  ok: "bg-emerald-50 border-emerald-200 text-emerald-800",
  err: "bg-red-50 border-red-200 text-red-800",
  warn: "bg-amber-50 border-amber-200 text-amber-800",
  info: "bg-blue-50 border-blue-200 text-blue-800",
};

const gatewayCardClass = (isSelected, isEnabled) => {
  if (isSelected && isEnabled) return "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200";
  if (isEnabled) return "border-gray-200 hover:border-gray-300";
  return "border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed";
};

const formatIDR = (n) => `Rp${Number(n).toLocaleString("id-ID")}`;

export default function Billing() {
  const { merchant, refresh } = useAuth();
  const [params] = useSearchParams();
  const [gateways, setGateways] = useState({ stripe: false, xendit: false, midtrans: false, packages_idr: [] });
  const [selectedGateway, setSelectedGateway] = useState("stripe");
  const [packagesUSD, setPackagesUSD] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const current = merchant?.subscription_plan || "starter";

  useEffect(() => {
    Promise.all([billing.gateways(), billing.packages()])
      .then(([g, p]) => {
        setGateways(g);
        setPackagesUSD(p);
        // pick first enabled gateway as default
        if (g.midtrans) setSelectedGateway("midtrans");
        else if (g.xendit) setSelectedGateway("xendit");
        else setSelectedGateway("stripe");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Poll Stripe payment status
  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) {
        setStatusMessage({ type: "warn", text: "Status pembayaran masih diproses. Cek email untuk konfirmasi." });
        return;
      }
      try {
        const res = await billing.status(sessionId);
        if (res.payment_status === "paid") {
          setStatusMessage({ type: "ok", text: `Pembayaran berhasil! Plan Anda upgrade ke ${res.plan}.` });
          toast.success("Subscription aktif!");
          if (refresh) refresh();
          return;
        }
        if (res.status === "expired") {
          setStatusMessage({ type: "err", text: "Session pembayaran kadaluarsa. Silakan coba lagi." });
          return;
        }
        attempts += 1;
        setTimeout(poll, 2000);
      } catch {
        setStatusMessage({ type: "err", text: "Gagal cek status pembayaran." });
      }
    };
    setStatusMessage({ type: "info", text: "Mengecek status pembayaran Stripe..." });
    poll();
  }, [params]);

  // Poll Xendit status
  useEffect(() => {
    const xId = params.get("xendit_id");
    if (!xId) return;
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) {
        setStatusMessage({ type: "warn", text: "Status Xendit masih diproses..." });
        return;
      }
      try {
        const res = await billing.xenditStatus(xId);
        if (res.payment_status === "paid") {
          setStatusMessage({ type: "ok", text: `Pembayaran Xendit berhasil! Plan: ${res.plan}` });
          toast.success("Subscription aktif via Xendit!");
          if (refresh) refresh();
          return;
        }
        attempts += 1;
        setTimeout(poll, 2500);
      } catch {
        setStatusMessage({ type: "err", text: "Gagal cek status Xendit." });
      }
    };
    setStatusMessage({ type: "info", text: "Mengecek status pembayaran Xendit..." });
    poll();
  }, [params]);

  const loadMidtransSnap = (clientKey, isProd) =>
    new Promise((resolve, reject) => {
      if (window.snap) return resolve(window.snap);
      const script = document.createElement("script");
      script.src = isProd ? "https://app.midtrans.com/snap/snap.js" : "https://app.sandbox.midtrans.com/snap/snap.js";
      script.setAttribute("data-client-key", clientKey || "");
      script.onload = () => resolve(window.snap);
      script.onerror = reject;
      document.body.appendChild(script);
    });

  const checkoutStripe = async (packageId) => {
    setCheckoutLoading(packageId);
    try {
      const origin = window.location.origin;
      const res = await billing.checkout(packageId, origin);
      window.location.href = res.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal buat checkout Stripe");
    }
    setCheckoutLoading(null);
  };

  const checkoutXendit = async (packageId) => {
    setCheckoutLoading(packageId);
    try {
      const origin = window.location.origin;
      const res = await billing.xenditInvoice(packageId, origin);
      if (res.url) window.location.href = res.url;
      else throw new Error("No invoice URL");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal buat invoice Xendit");
    }
    setCheckoutLoading(null);
  };

  const checkoutMidtrans = async (packageId) => {
    setCheckoutLoading(packageId);
    try {
      const res = await billing.midtransTx(packageId);
      const snap = await loadMidtransSnap(res.client_key, res.is_production);
      if (!snap) throw new Error("Snap.js gagal dimuat");
      snap.pay(res.token, {
        onSuccess: async () => {
          toast.success("Pembayaran diterima, memproses...");
          try {
            const st = await billing.midtransStatus(res.order_id);
            if (st.payment_status === "paid") {
              setStatusMessage({ type: "ok", text: `Pembayaran berhasil! Plan: ${st.plan}` });
              if (refresh) refresh();
            } else {
              setStatusMessage({ type: "info", text: "Menunggu konfirmasi Midtrans..." });
            }
          } catch (err) { console.warn("Midtrans status check failed:", err); }
        },
        onPending: () => setStatusMessage({ type: "info", text: "Menunggu pembayaran diselesaikan..." }),
        onError: () => toast.error("Pembayaran gagal"),
        onClose: () => setCheckoutLoading(null),
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Gagal buat transaksi Midtrans");
      setCheckoutLoading(null);
    }
  };

  const doCheckout = (planId) => {
    if (selectedGateway === "xendit") return checkoutXendit(planId);
    if (selectedGateway === "midtrans") return checkoutMidtrans(planId);
    return checkoutStripe(planId);
  };

  const enabledGateways = [
    { id: "midtrans", label: "Midtrans", desc: "IDR · Snap popup (VA, QRIS, e-wallet, kartu)", enabled: gateways.midtrans },
    { id: "xendit", label: "Xendit", desc: "IDR · Invoice (BCA, BNI, BRI, Mandiri, OVO, DANA, QRIS)", enabled: gateways.xendit },
    { id: "stripe", label: "Stripe", desc: "USD · Kartu kredit internasional (test mode)", enabled: gateways.stripe },
  ];

  const activePackages = selectedGateway === "stripe"
    ? packagesUSD.map((p) => ({ id: p.id, plan: p.plan, amount: p.amount, label: p.label, currency: "USD" }))
    : (gateways.packages_idr || []).map((p) => ({ id: p.id, plan: p.plan, amount: p.amount, label: p.label, currency: "IDR" }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">Billing & Subscription</h1>
        <p className="text-sm text-gray-500 mt-1">Upgrade plan kapan saja. Pilih gateway pembayaran sesuai kebutuhan.</p>
      </div>

      {statusMessage && (
        <div
          className={`p-4 rounded-lg border ${STATUS_STYLE[statusMessage.type] || STATUS_STYLE.info}`}
          data-testid="payment-status-msg"
        >
          {statusMessage.text}
        </div>
      )}

      {(params.get("cancelled") || params.get("status") === "failed") && (
        <div className="p-4 rounded-lg border bg-amber-50 border-amber-200 text-amber-800">
          Pembayaran dibatalkan. Silakan coba lagi.
        </div>
      )}

      <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
        <CardContent className="p-6 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-emerald-600 text-white flex items-center justify-center"><CreditCard /></div>
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-emerald-700">Plan Aktif</div>
              <div className="font-heading text-2xl font-bold text-gray-900 capitalize">{current}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-gray-200">
        <CardContent className="p-5">
          <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">Pilih Gateway Pembayaran</div>
          <div className="grid md:grid-cols-3 gap-2">
            {enabledGateways.map((gw) => (
              <button
                key={gw.id}
                disabled={!gw.enabled}
                onClick={() => setSelectedGateway(gw.id)}
                data-testid={`gateway-${gw.id}`}
                className={`p-3 rounded-lg border text-left transition-all ${gatewayCardClass(selectedGateway === gw.id, gw.enabled)}`}
              >
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm text-gray-900">{gw.label}</span>
                  {!gw.enabled && <Badge variant="outline" className="text-[9px]">Belum aktif</Badge>}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">{gw.desc}</div>
              </button>
            ))}
          </div>
          {!gateways.xendit && !gateways.midtrans && (
            <p className="mt-3 text-xs text-gray-500">
              Untuk aktivasi Xendit/Midtrans, hubungi admin platform. Stripe tersedia untuk test.
            </p>
          )}
        </CardContent>
      </Card>

      <div>
        <h3 className="font-heading font-semibold text-gray-900 mb-3">Pilih Plan</h3>
        {loading ? <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>
          : (
            <div className="grid md:grid-cols-3 gap-4">
              {activePackages.map((p) => {
                const isCurrent = p.plan === current;
                const isPopular = p.plan === "growth";
                return (
                  <Card key={p.id} className={`border ${isCurrent ? "border-emerald-500 shadow-sm" : "border-gray-200"} ${isPopular ? "border-amber-200" : ""}`} data-testid={`plan-card-${p.plan}`}>
                    <CardContent className="p-5">
                      {isPopular && <Badge className="bg-amber-500 text-white border-amber-500 mb-2"><Sparkles size={10} className="mr-1" /> Popular</Badge>}
                      <h4 className="font-heading text-lg font-bold text-gray-900 capitalize">{p.plan}</h4>
                      <div className="mt-1 text-sm text-gray-500">{p.label}</div>
                      <div className="mt-3 font-heading text-2xl font-bold text-gray-900">
                        {p.currency === "IDR" ? formatIDR(p.amount) : `$${p.amount}`}
                        <span className="text-xs text-gray-500 font-normal"> /bulan</span>
                      </div>
                      <ul className="mt-4 space-y-1.5 text-xs text-gray-700">
                        {(planFeatures[p.plan] || []).map((f) => (
                          <li key={f} className="flex items-start gap-1.5"><Check className="w-3 h-3 text-emerald-600 mt-0.5 flex-shrink-0" strokeWidth={2.5} />{f}</li>
                        ))}
                      </ul>
                      {isCurrent ? (
                        <div className="mt-4 inline-flex items-center gap-1 text-emerald-600 text-sm font-semibold"><Check size={14} /> Plan Anda</div>
                      ) : (
                        <Button
                          onClick={() => doCheckout(p.id)}
                          disabled={checkoutLoading === p.id}
                          className="mt-4 w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`checkout-${p.id}-${selectedGateway}`}
                        >
                          {checkoutLoading === p.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                          Upgrade ke {p.plan}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
      </div>

      <Card className="border-gray-200 bg-gray-50/60">
        <CardContent className="p-5">
          <div className="text-xs font-bold uppercase tracking-widest text-gray-500">Info Pembayaran</div>
          {selectedGateway === "stripe" && (
            <p className="mt-1 text-sm text-gray-700">
              Stripe Test mode: <code className="font-mono bg-white border border-gray-200 rounded px-1">4242 4242 4242 4242</code>, tanggal masa depan, CVV apapun.
            </p>
          )}
          {selectedGateway === "xendit" && (
            <p className="mt-1 text-sm text-gray-700">
              Xendit invoice mendukung Virtual Account (BCA, BNI, BRI, Mandiri, Permata), QRIS, & e-wallet (OVO, DANA, ShopeePay, LinkAja).
            </p>
          )}
          {selectedGateway === "midtrans" && (
            <p className="mt-1 text-sm text-gray-700">
              Midtrans Snap popup: Kartu kredit, VA semua bank, QRIS, GoPay, ShopeePay, dan lainnya dalam 1 halaman.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
